// features/branch-shift/permissions.ts
//
// Mirror gate của internal/core/branchshift/service.go. FE chỉ ẩn/hiện —
// chốt chặn thật ở BE: operate cấp rộng theo role nhưng service chỉ cho lead
// của ĐÚNG ca (hoặc trưởng cửa hàng chi nhánh đó) thao tác.
import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";
import { ROLE } from "@/lib/auth/roles";

type PermUser = Pick<Staff, "id" | "role"> | null | undefined;

export function canReadBranchShifts(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.BranchShiftRead);
}

/** Xếp lịch tuần + gán ca trưởng: trưởng cửa hàng / điều phối cũ / giám đốc. */
export function canPlanBranchShifts(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.BranchShiftPlan);
}

/** Nút mở/tick/đóng hiện khi có action; BE mới quyết có phải lead ca đó không. */
export function canOperateBranchShifts(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.BranchShiftOperate);
}

/**
 * Chấm đạt/chưa đạt ảnh bảng kiểm (quyết định 0100).
 *
 * KHÁC canOperateBranchShifts: người vận hành là người BỊ chấm. Vai sale /
 * staff / manager cố ý KHÔNG có quyền này — tự chấm bài mình thì dấu chấm
 * không còn nghĩa gì.
 */
export function canReviewChecklist(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.BranchShiftReview);
}

export function canWriteIncident(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.IncidentWrite);
}

export function canAckIncident(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.IncidentAck);
}

/**
 * Đổi trạng thái một phiếu lỗi đã có (đánh dấu đã xử lý / xác minh).
 *
 * KHÁC canWriteIncident: kia là quyền LẬP phiếu mới. Quản lý cụm có quyền này
 * mà không có quyền kia — và trước bản vá RBAC-01 hai nút trạng thái không hỏi
 * quyền nào cả, nên vai không có quyền vẫn thấy nút rồi ăn 403 từ máy chủ.
 */
export function canSetIncidentStatus(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.IncidentStatusWrite);
}

/**
 * Nút "Đánh dấu đã xử lý" của phiếu lỗi (06/10/2026): nhân viên thường (vai
 * staff) chỉ XEM lỗi. Vai đó vẫn giữ incident.status.write vì cửa khắc phục
 * dùng chung — người lo được chỉ định báo xử lý ở khối Khắc phục, nút do máy
 * chủ bật qua `canResolve`. Máy chủ chặn lại ở `SetIncidentStatus`.
 */
export function canMarkIncidentResolved(user: PermUser): boolean {
  return canSetIncidentStatus(user) && user?.role !== ROLE.Staff;
}

type VerifiableIncident = {
  subjectStaffId?: string | null | undefined;
  reporterStaffId?: string | undefined;
  witnesses?: ReadonlyArray<{ staffId: string }> | undefined;
};

/**
 * Xác minh một phiếu lỗi (QĐ 0244 · 6b). Quản lý cụm / giám đốc như cũ; QC
 * được xác minh phiếu NGƯỜI KHÁC lập — không phải phiếu mình ghi hay làm
 * chứng, không phải phiếu cũ chưa ghi mã người lập. Máy chủ gác lại lần nữa
 * (`qcDuocXacNhan`), kể cả rào nghề QC phụ trách.
 */
export function canVerifyIncident(user: PermUser, inc: VerifiableIncident): boolean {
  if (!user) return false;
  // QĐ 0333 (rà soát 15/10/2026, N6): người bị ghi, người lập, người làm chứng
  // không xác minh phiếu của chính mình — ở mọi vai. Máy chủ gác ở
  // `nguoiDuyetDocLap`; đây để khỏi thấy nút rồi ăn 403.
  if (user.id && (inc.subjectStaffId === user.id || inc.reporterStaffId === user.id)) return false;
  if (user.id && (inc.witnesses ?? []).some((w) => w.staffId === user.id)) return false;
  if (canAckIncident(user) && canSetIncidentStatus(user)) return true;
  if (user.role !== ROLE.QC || !canSetIncidentStatus(user)) return false;
  if (!inc.reporterStaffId || inc.reporterStaffId === user.id) return false;
  return !(inc.witnesses ?? []).some((w) => w.staffId === user.id);
}

/**
 * Duyệt / trả lại sổ SỐ MẶT trang điểm của một buổi (quyết định 0194 điều 5).
 *
 * KHÁC canPlanBranchShifts: đây là một cửa TIỀN, không phải cửa xếp lịch. Vai
 * mở được nó là trưởng cửa hàng · quản lý cụm · giám đốc · quản trị; phạm vi cơ
 * sở do máy chủ cắt tiếp theo cơ sở của chính buổi.
 */
export function canApproveMakeupFaces(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.MakeupFacesApprove);
}

export function canManageTemplates(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.ShiftTemplateWrite);
}

/** Người này có phải ca trưởng của bản ghi ca đang xét không (so staff.id). */
export function isLeadOf(user: PermUser, shift: { leadStaffId: string }): boolean {
  return !!user && user.id === shift.leadStaffId;
}
